import React, { useEffect, useState } from 'react';
import { useSetRecoilState } from 'recoil';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { useInterval } from 'usehooks-ts';

import './index.less';

import {
  ChatMessage,
  ChatMessageContentInner,
  MessageRole,
  ProjectModel,
  ProviderModel,
  SearchMessageModel,
  SearchPipelineOutput,
  SearchRunModel,
  SearchRunStatus,
  SearchThreadModel,
} from '../../../../../gamma-api';
import { alertsSelectorAdd } from '../../../../recoil';
import MessageList from '../../../../components/MessageList';
import { defaultApi } from '../../../../apis';
import IbTabPane from '../../../../components/common/IbTabPane';
import IbTabs from '../../../../components/common/IbTabs';
import Settings from '../../../../components/Settings';
import { LARGE_PAGE_SIZE } from '../../../../constants';
import { IMessageListItem, MessageListItemDirection } from '../../../../components/MessageListItem';
import { ProjectSearchPipelineSettingsLayout } from '../../../../components/Settings/ProjectSearchPipeline';
import { getChatMessageTextContent } from '../../../../utils/messageUtil';
import { PROJECT_AVATAR_NAME_DEFAULT, USER_AVATAR_NAME_DEFAULT } from '../const';

import AssistantMessageView from './AssistantMessageView';
import { buildThreadCreationRequest } from './utils';

const MAIN_CLASS_NAME = 'project-testing-chat-tab';
const HEADER_CLASS_NAME = `${MAIN_CLASS_NAME}__header`;
const CONTENT_CLASS_NAME = `${MAIN_CLASS_NAME}__content`;
const DIALOG_CONTENT_CLASS_NAME = `${CONTENT_CLASS_NAME}__dialog`;
const SETTINGS_CONTENT_CLASS_NAME = `${CONTENT_CLASS_NAME}__settings`;

const USER_MESSAGE_TEMPORARY_ID = 'user-message-temporary-id';
const RUN_CHECK_INTERVAL = 1000;

enum RightPanelTabKeys {
  CHUNKS = 'CHUNKS',
  SEARCH_SETTINGS = 'SEARCH_SETTINGS',
}

export interface ITestingChatTabProps {
  project?: ProjectModel;
  providers: ProviderModel[];
  providersLoading: boolean;
  onSearchPipelineSave: (searchPipeline: SearchPipelineOutput) => Promise<void>;
}

const TestingChatTab: React.FC<ITestingChatTabProps> = ({
  project,
  providers,
  providersLoading,
  onSearchPipelineSave,
}) => {
  const addAlert = useSetRecoilState(alertsSelectorAdd);
  const { t } = useTranslation();

  const [threadLoading, setThreadLoading] = useState(false);
  const [runCreation, setRunCreation] = useState(false);

  const [thread, setThread] = useState<SearchThreadModel>();
  const [messageList, setMessageList] = useState<SearchMessageModel[]>([]);
  const [run, setRun] = useState<SearchRunModel>();

  const [selectedMessage, setSelectedMessage] = useState<SearchMessageModel>();
  const [rightPanelTabKey, setRightPanelTabKey] = useState<RightPanelTabKeys>(RightPanelTabKeys.CHUNKS);
  const [searchPipeline, setSearchPipeline] = useState<SearchPipelineOutput | undefined>(project?.pipeline.search);

  const isRunProcessing =
    runCreation || run?.status == SearchRunStatus.Pending || run?.status == SearchRunStatus.Processing;

  const mapMessage = (message: SearchMessageModel): IMessageListItem => {
    const direction =
      message.chat_message.role === MessageRole.User
        ? MessageListItemDirection.OUTBOUND
        : MessageListItemDirection.INBOUND;

    return {
      id: message.message_id,
      senderName:
        direction === MessageListItemDirection.OUTBOUND ? t('Project: Testing chat: Sender name') : project?.name || '',
      senderAvatar:
        direction === MessageListItemDirection.OUTBOUND ? USER_AVATAR_NAME_DEFAULT : PROJECT_AVATAR_NAME_DEFAULT,
      content: getChatMessageTextContent(message.chat_message),
      isSelected: selectedMessage?.message_id === message.message_id,
      direction,
    };
  };

  const fetchMessages = async (thread: SearchThreadModel) => {
    try {
      const response = await defaultApi.searchUserMessagesSearchMessagesUserGet(
        null,
        [thread.thread_id],
        null,
        0,
        LARGE_PAGE_SIZE
      );
      setMessageList(response.data.items || []);
    } catch (err) {
      addAlert({ content: t('Failed to load messages'), type: 'error' });
      // eslint-disable-next-line no-console
      console.error(err);
    }
  };

  const tryFetchThread = async () => {
    if (!project) return;

    try {
      setThreadLoading(true);
      const threadResponse = await defaultApi.tryGetLastSearchThreadsLastGet([project.project_id]);
      const thread = threadResponse.data;
      if (thread) {
        setThread(thread);
        await fetchMessages(thread);
      }
    } catch (err) {
      addAlert({ content: t('Failed to load thread'), type: 'error' });
      // eslint-disable-next-line no-console
      console.error(err);
    } finally {
      setThreadLoading(false);
    }
  };

  const sendChatMessageToThread = async (chat_message: ChatMessage) => {
    if (!project) {
      return;
    }

    try {
      setRunCreation(true);
      const runThread = thread
        ? thread
        : (await defaultApi.createThreadSearchThreadsPost(buildThreadCreationRequest(project.project_id))).data.model;

      await defaultApi.createMessageSearchMessagesPost({
        thread_id: runThread.thread_id,
        chat_message,
      });

      const runResponse = await defaultApi.createRunSearchRunsPost({
        thread_id: runThread.thread_id,
        search_pipeline: searchPipeline,
      });
      setThread(runThread);
      setRun(runResponse.data.model);
    } catch (err) {
      addAlert({ content: t('Failed to create run'), type: 'error' });
      // eslint-disable-next-line no-console
      console.error(err);
    } finally {
      setRunCreation(false);
    }
  };

  const createNewThread = async () => {
    if (!project) {
      return;
    }

    try {
      const response = await defaultApi.createThreadSearchThreadsPost(buildThreadCreationRequest(project.project_id));
      setThread(response.data.model);
    } catch (err) {
      addAlert({ content: t('Failed to create thread'), type: 'error' });
      // eslint-disable-next-line no-console
      console.error(err);
    }
  };

  const checkRunState = async (thread: SearchThreadModel, run: SearchRunModel) => {
    try {
      const response = await defaultApi.getOneRunSearchRunsRunIdGet(run.run_id);
      const loadedRun = response.data;

      if (loadedRun.status == SearchRunStatus.Success) {
        await fetchMessages(thread);
        setRun(undefined);
        return;
      }

      if (loadedRun.status == SearchRunStatus.Error || loadedRun.status == SearchRunStatus.Failure) {
        addAlert({ content: t('Failed to process run'), type: 'error' });
        // eslint-disable-next-line no-console
        console.error(loadedRun.problem);
        setRun(undefined);
      }
    } catch (err) {
      addAlert({ content: t('Failed to load run'), type: 'error' });
      // eslint-disable-next-line no-console
      console.error(err);
    }
  };

  const onMessageSelect = (message: IMessageListItem) => {
    const foundMessage = messageList.find((m) => m.message_id === message.id);

    if (foundMessage?.chat_message.role == MessageRole.Assistant) {
      setSelectedMessage(foundMessage);
    }
  };

  const onMessageSend = (content: ChatMessageContentInner) => {
    if (isRunProcessing || threadLoading) {
      return;
    }

    const userMessage = {
      message_id: USER_MESSAGE_TEMPORARY_ID,
      created_on: moment().toISOString(),
      project_id: '',
      thread_id: '',
      chat_message: { role: MessageRole.User, content: [content] },
    } as SearchMessageModel;

    setMessageList([...messageList, userMessage]);
    sendChatMessageToThread(userMessage.chat_message).finally();
  };

  const onMessagesClean = () => {
    if (isRunProcessing || threadLoading) {
      return;
    }

    setMessageList([]);
    setSelectedMessage(undefined);
    createNewThread().finally();
  };

  const onRightPanelTabChange = (activeKey: string) => {
    setRightPanelTabKey(activeKey as RightPanelTabKeys);
  };

  const onSearchPipelineSettingsChange = (searchPipeline: SearchPipelineOutput) => {
    setSearchPipeline(searchPipeline);
  };

  const onSearchPipelineSettingsReset = () => {
    setSearchPipeline(project?.pipeline.search);
  };

  const onSearchPipelineSettingsSave = async () => {
    if (!searchPipeline) {
      return;
    }

    await onSearchPipelineSave(searchPipeline);
  };

  const onProjectPropChange = () => {
    setSearchPipeline(project?.pipeline.search);
    tryFetchThread().finally();
  };
  useEffect(onProjectPropChange, [project]);

  useInterval(() => {
    if (run && thread) {
      checkRunState(thread, run).finally();
    }
  }, RUN_CHECK_INTERVAL);

  return (
    <div className={MAIN_CLASS_NAME}>
      <div className={HEADER_CLASS_NAME}></div>
      <div className={CONTENT_CLASS_NAME}>
        <div className={DIALOG_CONTENT_CLASS_NAME}>
          <MessageList
            loading={threadLoading}
            messageList={messageList.map(mapMessage)}
            messageProcessing={isRunProcessing}
            onClean={onMessagesClean}
            onMessageSelect={onMessageSelect}
            onMessageSend={onMessageSend}
          />
        </div>
        <div className={SETTINGS_CONTENT_CLASS_NAME}>
          <IbTabs activeKey={rightPanelTabKey} onChange={onRightPanelTabChange}>
            <IbTabPane key={RightPanelTabKeys.CHUNKS} tab={t('Project: Testing chat: Chunks')}>
              <AssistantMessageView message={selectedMessage} />
            </IbTabPane>
            <IbTabPane key={RightPanelTabKeys.SEARCH_SETTINGS} tab={t('Project: Testing chat: Search settings')}>
              <Settings.ProjectSearchPipeline
                layout={ProjectSearchPipelineSettingsLayout.TESTING_CHAT}
                providers={providers}
                providersLoading={providersLoading}
                searchPipeline={searchPipeline}
                onChange={onSearchPipelineSettingsChange}
                onReset={onSearchPipelineSettingsReset}
                onSave={onSearchPipelineSettingsSave}
              />
            </IbTabPane>
          </IbTabs>
        </div>
      </div>
    </div>
  );
};

export default TestingChatTab;
